import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Users, Phone, MapPin, ShoppingCart, DollarSign, Search, Trophy, Star, Crown, Medal, Edit2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loyaltyRanking, setLoyaltyRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', phone: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [customersRes, rankingRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/loyalty/ranking?limit=10`).catch(() => ({ data: [] }))
      ]);
      setCustomers(customersRes.data);
      setLoyaltyRanking(rankingRes.data);
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async (searchTerm = '') => {
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const res = await axios.get(`${API}/customers${params}`);
      setCustomers(res.data);
    } catch (e) {
      toast.error('Erro ao carregar clientes');
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (value.length >= 2 || value.length === 0) {
      fetchCustomers(value);
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setEditForm({ name: customer.name, phone: customer.phone });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.name || !editForm.phone) {
      toast.error('Preencha todos os campos');
      return;
    }
    try {
      await axios.put(`${API}/customers/${selectedCustomer.id}`, editForm);
      toast.success('Cliente atualizado!');
      setShowEditModal(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar cliente');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/customers/${selectedCustomer.id}`);
      toast.success('Cliente excluído!');
      setShowDeleteDialog(false);
      setSelectedCustomer(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir cliente');
    }
  };

  const getPositionIcon = (position) => {
    if (position === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (position === 2) return <Medal className="w-5 h-5 text-gray-300" />;
    if (position === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-white/40 font-bold">{position}º</span>;
  };

  if (loading) {
    return (
      <DashboardLayout title="Clientes">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Clientes">
      <div className="space-y-6 pb-20 lg:pb-0">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-adegga">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Total de Clientes</p>
                  <p className="text-2xl font-bold text-white">{customers.length}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#ff6b00]/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#ff6b00]" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-adegga">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Clientes Fiéis</p>
                  <p className="text-2xl font-bold text-white">{loyaltyRanking.length}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-yellow-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Loyalty Ranking */}
        {loyaltyRanking.length > 0 && (
          <Card className="card-adegga border border-yellow-500/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Ranking de Clientes Fiéis
              </CardTitle>
              <p className="text-sm text-white/50">Top 10 clientes com mais pontos acumulados</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0.5 p-4">
                {loyaltyRanking.slice(0, 5).map((customer, index) => (
                  <div 
                    key={customer.customer_phone}
                    className={`p-4 rounded-lg ${
                      index === 0 
                        ? 'bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 border border-yellow-500/30' 
                        : index === 1 
                        ? 'bg-gradient-to-br from-gray-400/20 to-gray-500/10 border border-gray-400/30'
                        : index === 2
                        ? 'bg-gradient-to-br from-amber-600/20 to-amber-700/10 border border-amber-600/30'
                        : 'bg-[#0f0f15]'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 flex items-center justify-center">
                        {getPositionIcon(customer.position)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{customer.customer_name}</p>
                        <p className="text-xs text-white/40">{customer.order_count} pedidos</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-white/40">Pontos</p>
                        <p className="text-lg font-bold text-[#ff6b00]">{customer.total_points}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/40">Total Gasto</p>
                        <p className="text-sm text-green-400">R$ {customer.total_spent?.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {loyaltyRanking.length > 5 && (
                <div className="border-t border-white/5">
                  <Table>
                    <TableBody>
                      {loyaltyRanking.slice(5).map((customer) => (
                        <TableRow key={customer.customer_phone} className="border-white/5">
                          <TableCell className="w-12">
                            <span className="text-white/40 font-bold">{customer.position}º</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-[#ff6b00]/20 flex items-center justify-center text-[#ff6b00] font-bold text-xs">
                                {customer.customer_name?.charAt(0).toUpperCase()}
                              </div>
                              <span className="text-white">{customer.customer_name}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-[#ff6b00]/20 text-[#ff6b00]">
                              {customer.total_points} pts
                            </Badge>
                          </TableCell>
                          <TableCell className="text-white/60">
                            {customer.order_count} pedidos
                          </TableCell>
                          <TableCell className="text-green-400 text-right">
                            R$ {customer.total_spent?.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
            <Input
              placeholder="Buscar por nome ou telefone..."
              value={search}
              onChange={handleSearch}
              className="pl-10 bg-[#0f0f15] border-white/10 text-white"
            />
          </div>
        </div>

        {/* Customers Table */}
        <Card className="card-adegga">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-white/60">Cliente</TableHead>
                  <TableHead className="text-white/60">Telefone</TableHead>
                  <TableHead className="text-white/60">Pedidos</TableHead>
                  <TableHead className="text-white/60">Total Gasto</TableHead>
                  <TableHead className="text-white/60">Último Pedido</TableHead>
                  <TableHead className="text-white/60 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map(customer => (
                  <TableRow key={customer.id} className="border-white/5">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#ff6b00]/20 flex items-center justify-center text-[#ff6b00] font-bold text-sm">
                          {customer.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-white">{customer.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <a href={`tel:${customer.phone}`} className="text-[#ff6b00] flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {customer.phone}
                      </a>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-blue-500/20 text-blue-400">
                        {customer.order_count} pedidos
                      </Badge>
                    </TableCell>
                    <TableCell className="text-green-400 font-bold">
                      R$ {customer.total_spent?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell className="text-white/60">
                      {customer.last_order_at 
                        ? new Date(customer.last_order_at).toLocaleDateString('pt-BR')
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                          onClick={() => handleEdit(customer)}
                          data-testid={`edit-customer-${customer.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => {
                            setSelectedCustomer(customer);
                            setShowDeleteDialog(true);
                          }}
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {customers.length === 0 && (
              <div className="text-center py-12 text-white/40">
                Nenhum cliente encontrado
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Customer Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Editar Cliente</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-white/60 mb-1 block">Nome</label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-[#0f0f15] border-white/10 text-white"
                  placeholder="Nome do cliente"
                />
              </div>
              <div>
                <label className="text-sm text-white/60 mb-1 block">Telefone</label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="bg-[#0f0f15] border-white/10 text-white"
                  placeholder="Telefone"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowEditModal(false)}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleSaveEdit}>
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Excluir Cliente</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-white/60">
                Tem certeza que deseja excluir o cliente <strong className="text-white">{selectedCustomer?.name}</strong>?
              </p>
              <p className="text-sm text-yellow-400 mt-2">Esta ação não pode ser desfeita.</p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>Cancelar</Button>
              <Button 
                className="bg-red-600 hover:bg-red-700" 
                onClick={handleDelete}
                data-testid="confirm-delete-customer"
              >
                Excluir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Customers;
